import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

const SWIPE_THRESHOLD = 50
const SHOW_NAV_UI = true

/**
 * 圖片放大檢視。用 Portal 直接掛到 document.body，
 * 避免祖先的 relative + z-index 容器把它困在局部 stacking context 裡、被其他同層兄弟區塊蓋過去。
 *
 * 支援多張圖片時左右切換（按鈕、鍵盤方向鍵、觸控滑動）：傳入 images/index/onIndexChange。
 * 單張圖片時可直接傳 src（向下相容既有呼叫端）。
 */
export function Lightbox({ src, images, index = 0, onClose, onIndexChange }) {
  const list = images ?? (src ? [src] : [])
  const total = list.length
  const current = list[index]
  const touchStartX = useRef(null)

  const goPrev = () => {
    if (total <= 1) return
    onIndexChange?.((index - 1 + total) % total)
  }
  const goNext = () => {
    if (total <= 1) return
    onIndexChange?.((index + 1) % total)
  }

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') onClose?.()
      else if (e.key === 'ArrowLeft') goPrev()
      else if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  })

  if (!current) return null

  const handleTouchStart = e => {
    touchStartX.current = e.touches[0].clientX
  }
  const handleTouchEnd = e => {
    if (touchStartX.current == null) return
    const delta = e.changedTouches[0].clientX - touchStartX.current
    touchStartX.current = null
    if (delta > SWIPE_THRESHOLD) goPrev()
    else if (delta < -SWIPE_THRESHOLD) goNext()
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <button
        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-white/70 hover:text-white cursor-pointer z-10"
        onClick={onClose}
      >
        <X className="w-5 h-5" />
      </button>
      {SHOW_NAV_UI && total > 1 && (
        <>
          <button
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white cursor-pointer z-10 transition-colors"
            onClick={e => { e.stopPropagation(); goPrev() }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white cursor-pointer z-10 transition-colors"
            onClick={e => { e.stopPropagation(); goNext() }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      <img
        src={current}
        alt=""
        className="max-w-full max-h-full object-contain"
        onClick={e => e.stopPropagation()}
      />
    </div>,
    document.body
  )
}
